import React, { useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Avatar,
  IconButton,
  Typography,
  Grid,
  Button,
  Chip,
  Divider,
  Tooltip,
  Container
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  Comment,
  Share,
  DirectionsBike,
  Timer,
  Speed,
  Timeline,
  MoreVert
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const Feed = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([
    {
      id: 1,
      user: {
        username: 'cyclist_pro',
        avatar: null
      },
      title: 'Entrenamiento matutino 🚴‍♂️',
      content: 'Gran sesión de entrenamiento por la sierra. El tiempo perfecto y las piernas respondiendo bien! 💪',
      date: '24/03/2025 08:30',
      distance: 45.2,
      duration: 7200,
      avgSpeed: 22.6,
      maxSpeed: 42.3,
      elevation: 850,
      likes: 24,
      comments: 5,
      liked: false,
      tags: ['Entrenamiento', 'Montaña']
    },
    {
      id: 2,
      user: {
        username: 'maria_bikes',
        avatar: null
      },
      title: 'Nueva ruta descubierta 🗺️',
      content: 'Increíble ruta por la costa. Los paisajes son espectaculares y el viento a favor ayudó mucho 🌊',
      date: '24/03/2025 10:15',
      distance: 65.8,
      duration: 10800,
      avgSpeed: 24.3,
      maxSpeed: 48.5,
      elevation: 420,
      likes: 31,
      comments: 8,
      liked: true,
      tags: ['Ruta', 'Costa']
    },
    {
      id: 3,
      user: {
        username: 'team_velocity',
        avatar: null
      },
      title: 'Entrenamiento en grupo 🚴‍♂️🚴‍♀️',
      content: 'Excelente salida en grupo hoy. Trabajando en relevos y mejorando la coordinación del equipo. ¡Gracias a todos por venir! 🙌',
      date: '24/03/2025 07:00',
      distance: 85.3,
      duration: 12600,
      avgSpeed: 26.8,
      maxSpeed: 52.1,
      elevation: 980,
      likes: 45,
      comments: 12,
      liked: false,
      tags: ['Equipo', 'Entrenamiento', 'Velocidad']
    }
  ]);

  const handleLike = (postId) => {
    setPosts(posts.map(post => 
      post.id === postId ? { ...post, liked: !post.liked, likes: post.liked ? post.likes - 1 : post.likes + 1 } : post
    ));
  };

  const formatMetric = (value, unit) => {
    return `${value.toFixed(1)}${unit}`;
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <Container maxWidth="md" sx={{ 
      py: 4,
      mt: 6
    }}>
      <Grid container spacing={3}>
        {posts.map(post => (
          <Grid item xs={12} key={post.id}>
            <Card 
              sx={{ 
                background: 'rgba(24, 24, 28, 0.98)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)'
                }
              }}
            >
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {post.user.username[0].toUpperCase()}
                  </Avatar>
                }
                action={
                  <IconButton>
                    <MoreVert />
                  </IconButton>
                }
                title={
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'white' }}>
                    {post.user.username}
                  </Typography>
                }
                subheader={
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {post.date}
                  </Typography>
                }
              />
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1, color: 'white' }}>
                  {post.title}
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 3, 
                    color: 'rgba(255, 255, 255, 0.9)',
                    lineHeight: 1.6
                  }}
                >
                  {post.content}
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Tooltip title={t('distance')}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DirectionsBike sx={{ color: '#2196F3' }} />
                        <Typography sx={{ color: 'white' }}>
                          {formatMetric(post.distance, 'km')}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Tooltip title={t('duration')}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Timer sx={{ color: '#2196F3' }} />
                        <Typography sx={{ color: 'white' }}>
                          {formatTime(post.duration)}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Tooltip title={t('averageSpeed')}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Speed sx={{ color: '#2196F3' }} />
                        <Typography sx={{ color: 'white' }}>
                          {formatMetric(post.avgSpeed, 'km/h')}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Tooltip title={t('elevation')}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Timeline sx={{ color: '#2196F3' }} />
                        <Typography sx={{ color: 'white' }}>
                          {formatMetric(post.elevation, 'm')}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  {post.tags.map((tag, index) => (
                    <Chip 
                      key={index}
                      label={tag}
                      size="small"
                      sx={{ 
                        mr: 1,
                        mb: 1,
                        background: 'rgba(33, 150, 243, 0.1)',
                        border: '1px solid rgba(33, 150, 243, 0.3)',
                        color: '#2196F3',
                        '&:hover': {
                          background: 'rgba(33, 150, 243, 0.2)',
                          cursor: 'pointer'
                        }
                      }} 
                    />
                  ))}
                </Box>
              </CardContent>

              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              <CardActions sx={{ px: 2, py: 1 }}>
                <Button 
                  size="small" 
                  startIcon={post.liked ? <Favorite color="error" /> : <FavoriteBorder />}
                  onClick={() => handleLike(post.id)}
                  sx={{ 
                    color: post.liked ? 'error.main' : 'white',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                  {post.likes}
                </Button>
                <Button 
                  size="small" 
                  startIcon={<Comment />}
                  sx={{ 
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                  {post.comments}
                </Button>
                <Button 
                  size="small" 
                  startIcon={<Share />}
                  sx={{ 
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                  {t('share')}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Feed;
